import { DateTime } from 'luxon';
import React from 'react';

import { Link, rem, Text } from '@vimeo/bokeh';
import { translate } from '@vimeo/i18n-runtime';
import { vimeoConfig } from '@vimeo/live-core/configs/VimeoConfig';

export const rtmpTranslations = {
  add: translate('Add'),
  addVideos: translate('Add videos'),
  audioCodec: translate('Audio codec'),
  applicableSettings: translate(
    'Applicable settings from your event form will be added to your recording automatically. You can edit the form on the video page when the event ends.',
  ),

  autoStop: translate('Autostop'),
  avcLevel: translate('AVC level'),
  avcProfile: translate('AVC profile'),
  averageBitrate: translate('Average bitrate'),
  averageFrameRate: translate('Average frame rate'),
  backupStreamConnected: translate('Your backup stream is connected and ready to stream'),
  backupStreamKey: translate('Backup stream key'),
  backupStreamIsUnavailable: translate(
    'Backup stream is unavailable until your primary stream is connected',
  ),
  backUpStreamStatus: translate('Backup stream status'),
  backupStreamStreaming: translate('Your backup stream is streaming'),
  basics: translate('Basics'),
  bitrate: translate('Bitrate'),
  channel: translate('Channel'),
  connectSecondaryStream: translate('Connect a secondary stream as a backup'),
  connect: translate('Connect'),
  connectAsAdmin: translate('Connect as an admin'),
  connectAsMember: translate('Connect as a member'),
  connectEncoder: translate('Connect an encoder'),
  connectEncoderToBeginPreview: translate('Connect encoder to begin stream preview'),
  connectEncoderDescription: translate(
    'Copy the stream URL and stream key from Settings and paste it into your encoder. Once connected, the stream preview will appear here.',
  ),
  connectEncoderHelper: translate({
    replacements: {
      LINK: (children: string) => (
        <Text
          variant="body-xl"
          href={vimeoConfig.SUPPORT.ENCODER_GUIDE}
          fontSize={'text-sm'}
          target={'_blank'}
          cursor={'pointer'}
          textDecoration={'underline'}
          as={'a'}
        >
          {children}
        </Text>
      ),
    },
    singular:
      'To go live, start the stream from your encoder. {LINK}How to go live from an encoder{/LINK}',
  }),
  connectToLinkedin: translate('Connect to LinkedIn'),
  connected: translate('Connected'),
  connectedBy: translate('Connected by'),
  copy: translate('Copy'),
  copyRtmpUrl: translate('Copy RTMP URL'),
  createNew: translate('Create new'),
  currentStatus: translate('Current status'),
  custom: translate('Custom'),
  destinationBrokenNotice: translate(
    'Unable to stream. Ask the owner of this connection to reconnect it, or remove and connect a new destination.',
  ),
  destinationName: translate('Destination name'),
  destinationNameInvalidMessage: translate('Please enter a valid title for this destination'),
  destinations: translate('Destinations'),
  embedPrivacyDemoDisabledTip: translate('Demo event cannot be embedded'),
  ended: translate('Ended'),
  endEvent: translate('End event'),
  endEventDirectly: translate('End events directly from an external encoder'),
  existingLinkedinEventDate: (dateTime: DateTime) =>
    translate({
      replacements: {
        DATE: () => dateTime.toFormat('D'),
        TIME: () => dateTime.toFormat('t'),
      },
      singular: '{DATE} at {TIME}',
    }),
  extendedStreamSupportSrtNotice: translate('Extended streaming is not supported with SRT'),
  facebook: translate('Facebook'),
  facebookEveryone: translate('Public'),
  facebookFriends: translate('Friends'),
  facebookJustMe: translate('Just me'),
  facebookNoPagesAdded: translate('No pages added'),
  facebookPage: translate('Page'),
  facebookProfilePage: translate('Profile Page'),
  facebookRequirementsNotice: translate({
    replacements: {
      LINK: (children: string) => (
        <Text
          variant="body-xl"
          href={'https://www.facebook.com/business/help/167417030499767?id=1123223941353904'}
          fontSize={'text-sm'}
          color={'blue.500'}
          target={'_blank'}
          cursor={'pointer'}
          textDecoration={'underline'}
          as={'a'}
        >
          {children}
        </Text>
      ),
    },
    singular: 'This page does not meet Facebook’s streaming requirements. {LINK}Learn more{/LINK}',
  }),
  facebookStreamSettings: translate('Facebook streaming settings'),
  frameRate: translate('Frame rate'),
  generateEventAnalytics: translate('Generate event analytics'),
  goLive: translate('Go live'),
  goLiveFromYourEncoder: translate('Go live from your encoder'),
  goLiveFromYourEncoderToStartEvent: translate('Go live from your encoder to start the event'),
  good: translate('Good'),
  hideAdvancedAnalytics: translate('Hide advanced analytics'),
  ifYouHaventAlreadyVerifyYoutube: translate(
    'If you haven’t already, verify your YouTube account so that you can go live',
  ),
  instagram: translate('Instagram'),
  instagramStreamingSettings: translate('Instagram streaming settings'),
  languageStreams: translate('Language streams'),
  loadMore: translate('Load more'),
  lowResView: translate('Low-res view'),
  linkedin: translate('LinkedIn'),
  linkedinAllowsYouToGoLive: translate(
    'LinkedIn allows you to go live any time from 15 minutes before to 2 hours after your scheduled start time. Visit LinkedIn to make changes to scheduled events.',
  ),
  linkedinDoesntSupportEditingStartTime: translate(
    'LinkedIn doesn’t support editing the start time of existing events. Create a new event to schedule a start time.',
  ),
  linkedInDailyStreamingLimit: translate(
    'Members are currently limited to scheduling up to 10 scheduled live events per day (including both your profile and any Pages you manage).',
  ),
  linkedinReconnectAsMemberMessage: translate(
    'You do not have any company pages to stream to as an admin. To stream to your profile, connect as a member.',
  ),
  linkedinStreamingSettings: translate('LinkedIn streaming settings'),
  linkedinVideoDropdownPlaceholderText: translate('Select an event'),
  managePrivacy: translate('Manage privacy'),
  manageSettings: translate('Manage settings'),
  noPagesAdded: translate('No pages added'),
  notAvailable: translate('Not available'),
  notConnected: translate('Not connected'),
  now: translate('now'),
  onCopy: translate('Link copied'),
  onlyTeamOwnerCanConnectDestinations: translate(
    'Only the Team Owner can connect new destinations',
  ),
  onlyUserWhoConnectedDestinationCanManage: translate(
    'Only the user who connected this destination can manage it',
  ),
  ott: translate('OTT'),
  ottSettings: translate('OTT Settings'),
  ottStreamSettings: translate('OTT streaming settings'),
  ottNoChannelsAdded: translate('No channels added'),
  pleaseContactUsToUpgradePlan: translate('Please, contact us to upgrade plan'),
  previewStreamBeforeGoingLive: translate('Preview stream before going live'),
  previewStreamBeforeGoingLiveTooltip: translate('When on, select Go live to start event'),
  primary: translate('Primary'),
  privacySettings: translate('Privacy settings'),
  private: translate('Private'),
  preview: translate('Preview'),
  previewYourStreamAndGoLiveFromHere: translate({
    singular: 'When on, select {STRONG}Go live{/STRONG} to start event',
    replacements: {
      STRONG: (children: string) => <strong>{children}</strong>,
    },
  }),
  privacy: translate('Privacy'),
  privacyCannotBeUpdatedWhileLive: translate('Privacy cannot be updated while stream is live'),
  production: translate('Production'),
  protocolNotSupportedNotice: (protocol: string) =>
    translate({
      replacements: {
        PROTOCOL: protocol,
      },
      singular: 'Fail-safe streaming is not supported with {PROTOCOL}',
    }),
  public: translate('Public'),
  reachedStreamingLimit: translate("You've reached the streaming limit. Upgrade to go live."),
  reachedStreamingLimitDemo: translate(
    "You've reached the streaming limit for your demo. Upgrade to go live.",
  ),
  ready: translate('Ready'),
  reconnect: translate('Reconnect'),
  remove: translate('Remove'),
  removeFromThisEvent: translate('Remove from this event'),
  restartBroadcastAndTryAgain: translate('Please restart your broadcast and try again'),
  rtmp: translate('RTMP'),
  rtmps: translate('RTMPS'),
  rtmpStreamingSettings: translate('RTMP streaming settings'),
  rtmpsURL: translate('RTMPS URL'),
  rtmpURL: translate('RTMP URL'),
  rtmpURLHint: translate('Enter a URL starting with rtmp:// or rtmps://'),
  rtmpURLInvalidMessage: translate(
    'Please enter a valid RTMP URL in the format of rtmp://host/path',
  ),
  save: translate('Save'),
  schedule: translate('Schedule'),
  sessionId: translate('Session ID'),
  selectStartTimeThatsAtLeast: translate("Select a start time that's at least 1 minute from now"),
  sendFollowUpEmailNow: translate('Send follow-up email now'),
  shareLink: translate('Share link'),
  showAdvancedAnalytics: translate('Show advanced analytics'),
  simuliveTip: translate(
    'Simulated live events will automatically go live with pre-recorded videos at the selected time and date',
  ),
  srtHelpNotification: (url: string) =>
    translate({
      replacements: {
        LINK: (children: string) => (
          <Link
            href={url}
            target={'_blank'}
            fontSize={rem(12)}
            textDecoration={'underline'}
            cursor={'pointer'}
          >
            {children}
          </Link>
        ),
      },
      singular: '{LINK}How to set up a stream with SRT{/LINK}',
    }),
  stop: translate('Stop'),
  stream: translate('Stream'),
  streaming: translate('Streaming'),
  streamKey: translate('Stream key'),
  streamKeyInvalidMessage: translate('Please enter a valid stream key'),
  streamKeyDuplicate: translate(
    'A destination with this RTMP URL and stream key has already been added to this event',
  ),
  streamHealth: translate('Stream health'),
  streamHealthUnavailable: translate('Stream health data is currently unavailable'),
  streamMethodConfirmationBody: translate(
    'Some of your current settings may not be supported. Your settings will be preserved if you decide to switch back.',
  ),
  streamMethodConfirmationTitle: translate('Switch broadcast method'),
  streamMetrics: translate('Stream metrics'),
  streamName: translate('Stream name'),
  streamPreview: translate('Stream preview'),
  streamPreviewEndedDueInactivity: translate(
    'Stream preview ended due to inactivity. Restart the stream from your encoder to resume.',
  ),
  streamTo: translate('Stream to'),
  streamToYourProfilePage: translate('Stream to your profile page'),
  streamToYourCompanyPage: translate('Stream to your company page'),
  switch: translate('Switch'),
  thisWillStopStream: translate('This will stop the stream and end the event for your audience'),
  tikTok: translate('TikTok'),
  tikTokStreamingSettings: translate('TikTok streaming settings'),
  toSaveBandwidth: translate(
    'To save bandwidth, this is a low-resolution view. Your audience will see the high-quality stream.',
  ),
  turnOnRecordings: translate('Turn on registration for recording'),
  twitterX: translate('X'),
  unknown: translate('Unknown'),
  unlisted: translate('Unlisted'),
  unstable: translate('Unstable'),
  useExistingLinkedinEvents: translate('Use existing LinkedIn events'),
  videoCodec: translate('Video codec'),
  videoResolution: translate('Video resolution'),
  viewSettings: translate('View settings'),
  viewersMayExperienceBufferingProblems: translate(
    'Viewers may experience buffering problems. Check your available bandwidth and connectivity to Vimeo.',
  ),
  viewPrivacyDemoDisabledTip: translate('Demo event privacy cannot be changed'),
  xStreamingSettings: translate('X streaming settings'),
  youtubeErrorNoLivestreaming: translate(
    'Unable to stream. Please visit your YouTube channel features page and enable Live Streaming',
  ),
  youtubeStreamingSettings: translate('YouTube streaming settings'),
  yourConnectionReadyForStreaming: translate('Your connection is ready for streaming'),
  youHaveStableStream: translate('You have a stable stream to Vimeo'),
  youtube: translate('YouTube'),
};
